import { useCallback, useState, useMemo, useRef } from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import type { CategoryGroup } from '../types';
import { PaletteCreationModal } from './PaletteCreationModal';
import styles from './ChartThemePicker.module.css';

export type ChartThemePickerProps = {
  selectedThemeId: number | null;
  onThemeSelect: (themeId: number | null) => void;
  onCreateTheme?: ((name: string, colors: string[], categoryId: number) => void) | null;
  categories: CategoryGroup[];
  isLoading?: boolean;
};

export const ChartThemePicker = ({
  selectedThemeId,
  onThemeSelect,
  onCreateTheme,
  categories,
  isLoading = false,
}: ChartThemePickerProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const open = Boolean(anchorEl);

  // Get selected theme details
  const selectedTheme = useMemo(() => {
    if (!selectedThemeId) return null;
    for (const categoryGroup of categories) {
      const theme = categoryGroup.items.find(item => item.id === selectedThemeId);
      if (theme) return { theme, categoryName: categoryGroup.category.name };
    }
    return null;
  }, [selectedThemeId, categories]);

  const handleOpenMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleThemeChange = useCallback((themeId: number | null) => {
    onThemeSelect(themeId);
    handleCloseMenu();
  }, [onThemeSelect, handleCloseMenu]);

  const handleCreateClick = useCallback(() => {
    setIsModalOpen(true);
    handleCloseMenu();
  }, [handleCloseMenu]);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleModalSave = useCallback((name: string, colors: string[], categoryId: number) => {
    onCreateTheme?.(name, colors, categoryId);
  }, [onCreateTheme]);

  // Get initial colors and category from current theme or defaults
  const initialColors = useMemo(() => {
    const currentTheme = selectedTheme?.theme;
    return currentTheme?.colors || [
      '#4A90E2', '#50E3C2', '#F5A623', '#E94B3C', '#9013FE',
      '#BD10E0', '#7ED321', '#D0021B', '#F8E71C', '#B8E986',
      '#417505', '#F5A623', '#D0021B', '#4A90E2', '#9013FE'
    ];
  }, [selectedTheme]);

  const defaultCategoryId = useMemo(() => {
    // Default to the category of the currently selected theme, or first category
    return selectedTheme?.categoryName 
      ? categories.find(c => c.category.name === selectedTheme.categoryName)?.category.id
      : categories[0]?.category.id || 1;
  }, [selectedTheme, categories]);

  if (isLoading || !categories.length) {
    return null;
  }

  return (
    <div className={styles.container}>
      <label className={styles.label}>Chart Theme</label>

      <button
        ref={buttonRef}
        className={styles.selectButton}
        onClick={handleOpenMenu}
        type="button"
      >
        <span className={styles.selectButtonText}>
          {selectedTheme ? selectedTheme.theme.name : 'Default Colors'}
        </span>
        {selectedTheme && (
          <div className={styles.selectButtonColors}>
            {selectedTheme.theme.colors.slice(0, 8).map((color, index) => (
              <div
                key={index}
                className={styles.miniSwatch}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
        )}
        <KeyboardArrowDown className={styles.selectButtonIcon} />
      </button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          style: {
            minWidth: 500,
            maxHeight: 400,
          },
        }}
      >
        <MenuItem
          onClick={() => handleThemeChange(null)}
          selected={!selectedThemeId}
          className={styles.menuItem}
        >
          <span>Default Colors</span>
        </MenuItem>

        {categories.map((categoryGroup, catIndex) => (
          <div key={categoryGroup.category.id}>
            {catIndex > 0 && <div className={styles.categoryDivider} />}
            <div className={styles.categoryHeader}>
              {categoryGroup.category.name}
            </div>
            {categoryGroup.items.map(theme => (
              <MenuItem
                key={theme.id}
                onClick={() => handleThemeChange(theme.id)}
                selected={theme.id === selectedThemeId}
                className={styles.menuItem}
              >
                <span className={styles.themeName}>{theme.name}</span>
                <div className={styles.themeColors}>
                  {theme.colors.slice(0, 15).map((color, index) => (
                    <div
                      key={index}
                      className={styles.colorDot}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </MenuItem>
            ))}
          </div>
        ))}
      </Menu>

      {onCreateTheme && (
        <>
          <button
            className={styles.createButton}
            onClick={handleCreateClick}
            type="button"
          >
            + Create Theme
          </button>

          <PaletteCreationModal
            open={isModalOpen}
            onClose={handleModalClose}
            onSave={handleModalSave}
            initialColors={initialColors}
            categories={categories}
            defaultCategoryId={defaultCategoryId}
            ModalComponent={Dialog}
          />
        </>
      )}
    </div>
  );
};

ChartThemePicker.displayName = 'ChartThemePicker';
